import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CORARiskDataByNode, CORARiskDataPerEXSD } from 'src/features/types/coraTypes';
import { isAFTXCustomer } from 'src/utils/aftxDetection';
import './cora.css';

interface CORATableProps {
    data: CORARiskDataByNode[];
    processPathFamily?: 'Singles' | 'Multis';
}

const LABEL_COLUMN_WIDTH = 40;

const CORATable: React.FC<CORATableProps> = ({ data, processPathFamily }) => {
    const [hiddenRows, setHiddenRows] = useState<Set<string>>(new Set());

    const getTableData = (nodeData: CORARiskDataByNode): CORARiskDataPerEXSD[] => {
        if (!nodeData.riskDataPerEXSDByPPG) {
            return nodeData.riskDataPerEXSD || [];
        }

        const ppgData = nodeData.riskDataPerEXSDByPPG;
        
        if (processPathFamily === 'Singles' && ppgData.OUTBOUND_SINGLES) {
            return ppgData.OUTBOUND_SINGLES;
        }
        
        if (processPathFamily === 'Multis' && ppgData.OUTBOUND_MULTIS) {
            return ppgData.OUTBOUND_MULTIS;
        }
        
        if (ppgData.OUTBOUND_SINGLES && ppgData.OUTBOUND_MULTIS) {
            const singles = ppgData.OUTBOUND_SINGLES;
            const multis = ppgData.OUTBOUND_MULTIS;
            
            const aggregated: CORARiskDataPerEXSD[] = [];
            
            singles.forEach(singleItem => {
                const matchingMulti = multis.find(multiItem => multiItem.exsd === singleItem.exsd);
                
                if (matchingMulti) {
                    const aggregatedItem = {
                        ...singleItem,
                        remainingUnitsDueOutForCPT: (singleItem.remainingUnitsDueOutForCPT || 0) + (matchingMulti.remainingUnitsDueOutForCPT || 0),
                        unitsPlannedForCPT: (singleItem.unitsPlannedForCPT || 0) + (matchingMulti.unitsPlannedForCPT || 0),
                        expectedNewDropsForCPT: (singleItem.expectedNewDropsForCPT || 0) + (matchingMulti.expectedNewDropsForCPT || 0),
                        totalUnitsWithoutNewDropsForCPT: (singleItem.totalUnitsWithoutNewDropsForCPT || 0) + (matchingMulti.totalUnitsWithoutNewDropsForCPT || 0),
                        totalProjectedUnitsDueOutForCPT: (singleItem.totalProjectedUnitsDueOutForCPT || 0) + (matchingMulti.totalProjectedUnitsDueOutForCPT || 0),
                        cumulativePlannedUnitsDueOutForCPT: (singleItem.cumulativePlannedUnitsDueOutForCPT || 0) + (matchingMulti.cumulativePlannedUnitsDueOutForCPT || 0),
                        cumulativeProjectedUnitsDueOutUntilCPT: (singleItem.cumulativeProjectedUnitsDueOutUntilCPT || 0) + (matchingMulti.cumulativeProjectedUnitsDueOutUntilCPT || 0),
                        cumulativeShipCapacityUntilCPT: (singleItem.cumulativeShipCapacityUntilCPT || 0) + (matchingMulti.cumulativeShipCapacityUntilCPT || 0),
                        cumulativePlannedUnitsAsPercentOfCumulativeShipCapacity:
                            ((singleItem.cumulativePlannedUnitsDueOutForCPT || 0) + (matchingMulti.cumulativePlannedUnitsDueOutForCPT || 0)) / 
                            ((singleItem.cumulativeShipCapacityUntilCPT || 0) + (matchingMulti.cumulativeShipCapacityUntilCPT || 0)) * 100,
                        cumulativeProjectedUnitsAsPercentOfCumulativeShipCapacity: 
                            ((singleItem.cumulativeProjectedUnitsDueOutUntilCPT || 0) + (matchingMulti.cumulativeProjectedUnitsDueOutUntilCPT || 0)) / 
                            ((singleItem.cumulativeShipCapacityUntilCPT || 0) + (matchingMulti.cumulativeShipCapacityUntilCPT || 0)) * 100
                    };
                    (aggregatedItem as any)._isAggregated = true;
                    aggregated.push(aggregatedItem);
                } else {
                    aggregated.push(singleItem);
                }
            });
            
            return aggregated;
        }
        
        if (ppgData.ALL) return ppgData.ALL;
        if (ppgData.OUTBOUND_MULTIS) return ppgData.OUTBOUND_MULTIS;
        if (ppgData.OUTBOUND_SINGLES) return ppgData.OUTBOUND_SINGLES;
        
        return [];
    };

    const toggleRowVisibility = (rowKey: string) => {
        setHiddenRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowKey)) {
                newSet.delete(rowKey);
            } else {
                newSet.add(rowKey);
            }
            return newSet;
        });
    };

    const resetView = () => {
        setHiddenRows(new Set());
    };

    const tableData = useMemo(() => {
        const flattened: Array<CORARiskDataPerEXSD & { nodeId: string; nodeName?: string }> = [];

        data.forEach(nodeData => {
            const riskDataArray = getTableData(nodeData);
            riskDataArray.forEach(riskData => {
                flattened.push({
                    ...riskData,
                    nodeId: nodeData.nodeId,
                    nodeName: nodeData.nodeName
                });
            });
        });

        return flattened.sort((a, b) => {
            const dateA = a.exsd;
            const dateB = b.exsd;
            return new Date(dateA || '').getTime() - new Date(dateB || '').getTime();
        });
    }, [data, processPathFamily]);

    // Count leading columns inside Fast Track SLA (expectedNewDropsForCPT === 0)
    const fastTrackSLAColumnCount = useMemo(() => {
        let count = 0;
        for (const item of tableData) {
            if ((item.expectedNewDropsForCPT ?? 0) === 0) {
                count++;
            } else {
                break;
            }
        }
        return count;
    }, [tableData]);

    const formatDate = (item: CORARiskDataPerEXSD) => {
        const dateString = item.exsd;
        const date = new Date(dateString || '');
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).replace(', ', '<br>');
    };

    const formatNumber = (value: number | undefined) =>
        value !== undefined ? value.toLocaleString() : '-';

    const formatPercentage = (value: number | string | undefined, isAggregated = false) => {
        if (value === undefined || value === null) return '-';
        const numValue = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(numValue)) return '-';
        if (!isFinite(numValue)) return '∞';
        const displayValue = isAggregated ? numValue : numValue / 100;
        return `${displayValue.toFixed(2)}%`;
    };

    const formatLabelWithBoldMath = (label: string) => {
        return label.replace(/(\[[^\]]+\])/g, '<strong>$1</strong>');
    };

    const ExpectedNewChargeTooltip = () => {
        const isAFTX = isAFTXCustomer();
        const [show, setShow] = useState(false);
        const iconRef = useRef<HTMLSpanElement>(null);
        const [pos, setPos] = useState({ top: 0, left: 0 });
        const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

        const showTooltip = () => {
            if (hideTimeout.current) { clearTimeout(hideTimeout.current); hideTimeout.current = null; }
            if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setPos({ top: rect.bottom + 4, left: rect.left });
            }
            setShow(true);
        };

        const scheduleHide = () => {
            hideTimeout.current = setTimeout(() => setShow(false), 150);
        };

        return (
            <>
                <span
                    ref={iconRef}
                    onMouseEnter={showTooltip}
                    onMouseLeave={scheduleHide}
                    style={{ cursor: 'pointer', marginLeft: 4 }}
                >&#9432;</span>
                {show && createPortal(
                    <span
                        className="cora-portal-tooltip"
                        onMouseEnter={showTooltip}
                        onMouseLeave={scheduleHide}
                        style={{
                            position: 'fixed',
                            top: pos.top,
                            left: pos.left,
                            zIndex: 9999,
                        }}
                    >
                        {isAFTX
                            ? 'For any discrepancies in this data please submit your issues on the RIVER Tool.'
                            : <>
                                For any discrepancies in this data please reach out to the FCDemandForecasterService team as they own this data.
                                {' '}<a href="https://oncall.corp.amazon.com/#/view/aft-cumberland-oncall/schedule" target="_blank" rel="noopener noreferrer">Oncall</a>
                                {' | '}<a href="https://cti.amazon.com/cti/category/Distribution%20Center/type/AFT%20Cumberland/item/General" target="_blank" rel="noopener noreferrer">CTI</a>
                            </>
                        }
                    </span>,
                    document.body
                )}
            </>
        );
    };

    const getCellClass = (item: CORARiskDataPerEXSD & { _isAggregated?: boolean }, type: 'utilization' | 'projected') => {
        const rawUtilization = item.cumulativePlannedUnitsAsPercentOfCumulativeShipCapacity || 0;
        const rawProjectedUtilization = item.cumulativeProjectedUnitsAsPercentOfCumulativeShipCapacity || 0;
        
        const utilization = item._isAggregated ? rawUtilization : rawUtilization / 100;
        const projectedUtilization = item._isAggregated ? rawProjectedUtilization : rawProjectedUtilization / 100;

        if (utilization >= 98) return 'alert-cell'; // Critical - red
        if (utilization >= 75 && projectedUtilization >= 95) return 'alert-cell'; // High risk - red

        return type === 'utilization' ? 'brown-cell' : 'pink-cell';
    };

    if (!data || data.length === 0) {
        return (
            <div></div>
        );
    }

    const metrics = [
        {
            key: 'A',
            label: 'Remaining Units Due Out for CPT',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.remainingUnitsDueOutForCPT)
        },
        {
            key: 'B',
            label: 'Units Planned For CPT',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.unitsPlannedForCPT)
        },
        {
            key: 'C',
            label: 'Expected New Charge',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.expectedNewDropsForCPT)
        },
        {
            key: 'D',
            label: 'Total Units Considering No New Drops For CPT [A + B]',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.totalUnitsWithoutNewDropsForCPT)
        },
        {
            key: 'E',
            label: 'Total Projected Units Due Out For CPT [A + Max(B,C)]',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.totalProjectedUnitsDueOutForCPT)
        },
        {
            key: 'F',
            label: 'Cumulative Units Without New Charge Due Out Until CPT [Sum(D)]',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.cumulativePlannedUnitsDueOutForCPT)
        },
        {
            key: 'G',
            label: 'Cumulative Projected Units Due Out Until CPT [Sum(E)]',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.cumulativeProjectedUnitsDueOutUntilCPT)
        },
        {
            key: 'H',
            label: 'Cumulative Ship Capacity Until CPT',
            getValue: (item: CORARiskDataPerEXSD) => formatNumber(item.cumulativeShipCapacityUntilCPT)
        },
        {
            key: 'I',
            label: '% Utilization Cumulative (With No New Charge) [(F/H)]',
            getValue: (item: CORARiskDataPerEXSD & { _isAggregated?: boolean }) => formatPercentage(item.cumulativePlannedUnitsAsPercentOfCumulativeShipCapacity, item._isAggregated),
            getCellClass: (item: CORARiskDataPerEXSD) => getCellClass(item, 'utilization'),
            headerClass: 'orange-cell'
        },
        {
            key: 'J',
            label: '% Utilization Cumulative (With Expected New Charge) [(G/H)]',
            getValue: (item: CORARiskDataPerEXSD & { _isAggregated?: boolean }) => formatPercentage(item.cumulativeProjectedUnitsAsPercentOfCumulativeShipCapacity, item._isAggregated),
            getCellClass: (item: CORARiskDataPerEXSD) => getCellClass(item, 'projected'),
            headerClass: 'pink-cell'
        }
    ];

    return (
        <div className="cora-table-scroll-container" style={{ overflowX: 'auto', width: '100%', border: '1px solid transparent' }}>
            <table
                className="table table-condensed table-bordered"
                style={{
                    borderCollapse: 'collapse',
                    width: 'max-content'
                }}
            >
                <tbody>
                    {/* Header row: LABEL + CPT + dates as columns (final rotated structure) */}
                    <tr style={{ fontWeight: 'bold' }}>
                        <td
                            style={{
                                border: '1px solid #888888',
                                padding: '3px 6px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                backgroundColor: '#f2f2f2',
                                width: LABEL_COLUMN_WIDTH,
                                minWidth: LABEL_COLUMN_WIDTH,
                                maxWidth: LABEL_COLUMN_WIDTH
                            }}
                            onClick={resetView}
                            title="Click to show all rows"
                        >
                            LABEL<br />
                            <span style={{ fontSize: '0.75em' }}>
                                {hiddenRows.size > 0 ? '(click to show all)' : '(click labels to hide)'}
                            </span>
                        </td>
                        <td className="nowrap" style={{ whiteSpace: 'nowrap', minWidth: 'max-content', border: '1px solid #888888', padding: '3px 6px', position: 'sticky', left: LABEL_COLUMN_WIDTH, zIndex: 2, backgroundColor: '#f2f2f2' }}>CPT</td>
                        {tableData.map((item, index) => (
                            <td
                                key={index}
                                dangerouslySetInnerHTML={{ __html: formatDate(item) }}
                                style={{
                                    backgroundColor: (index + 3) % 2 === 0 ? '#e5e5e5' : 'transparent',
                                    border: '1px solid #888888',
                                    padding: '3px 6px',
                                    ...(index < fastTrackSLAColumnCount ? { opacity: 0.4 } : {})
                                }}
                            />
                        ))}
                    </tr>

                    {/* Each metric (A-J) becomes a row with dates as columns */}
                    {metrics.map((metric) => (
                        <tr
                            key={metric.key}
                            className="risk-data-row"
                            style={{ display: hiddenRows.has(metric.key) ? 'none' : 'table-row' }}
                        >
                            <td
                                className={`risk-data-label ${(metric.key === 'I' || metric.key === 'J') ? '' : (metric.headerClass || '')}`}
                                style={{
                                    border: '1px solid #cccccc',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1,
                                    width: LABEL_COLUMN_WIDTH,
                                    minWidth: LABEL_COLUMN_WIDTH,
                                    maxWidth: LABEL_COLUMN_WIDTH,
                                    ...(metric.key === 'I' ? { backgroundColor: '#ffddc5' } :
                                        metric.key === 'J' ? { backgroundColor: '#fff7d3' } :
                                        { backgroundColor: '#f2f2f2' })
                                }}
                                onClick={() => toggleRowVisibility(metric.key)}
                                title={`Click to ${hiddenRows.has(metric.key) ? 'show' : 'hide'} row ${metric.key}`}
                            >
                                ({metric.key})
                            </td>
                            <td
                                className="nowrap"
                                style={{
                                    whiteSpace: 'nowrap',
                                    minWidth: 'max-content',
                                    border: '1px solid #888888',
                                    padding: '3px 6px',
                                    position: 'sticky',
                                    left: LABEL_COLUMN_WIDTH,
                                    zIndex: 3,
                                    ...(metric.key === 'I' ? { backgroundColor: '#ffddc5' } :
                                        metric.key === 'J' ? { backgroundColor: '#fff7d3' } :
                                        { backgroundColor: '#fff' })
                                }}
                            >
                                <span dangerouslySetInnerHTML={{ __html: formatLabelWithBoldMath(metric.label) }} />
                                {metric.key === 'C' && <ExpectedNewChargeTooltip />}
                            </td>
                            {tableData.map((item, index) => {
                                const cellClass = metric.getCellClass ? metric.getCellClass(item) : '';
                                const isStripedColumn = (index + 3) % 2 === 0; // Starting from 4th column (index 2 + 1)

                                let backgroundColor = '';
                                
                                if (cellClass === 'alert-cell') {
                                    backgroundColor = (isStripedColumn && metric.key !== 'I' && metric.key !== 'J') ? '#e5c5c5' : '#ffcccc';
                                } else if (metric.key === 'I') {
                                    backgroundColor = '#ffddc5';
                                } else if (metric.key === 'J') {
                                    backgroundColor = '#fff7d3';
                                } else if (isStripedColumn) {
                                    if (cellClass === 'brown-cell') {
                                        backgroundColor = '#e5d5c5';
                                    } else if (cellClass === 'pink-cell') {
                                        backgroundColor = '#e5e7d3';
                                    } else {
                                        backgroundColor = '#e5e5e5';
                                    }
                                }

                                return (
                                    <td
                                        key={index}
                                        className={`nowrap ${(metric.key === 'I' || metric.key === 'J') ? '' : cellClass}`}
                                        style={{
                                            border: '1px solid #888888',
                                            padding: '3px 6px',
                                            ...(backgroundColor ? { backgroundColor } : {}),
                                            ...(index < fastTrackSLAColumnCount ? { opacity: 0.4 } : {})
                                        }}
                                    >
                                        {metric.getValue(item)}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default CORATable;
